import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMixpanel } from 'react-mixpanel-browser';

import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { SocietyAiPlanTier } from 'types/Society';
import { setModal } from 'modules/_shared/redux/modalActions';
import cleanText from 'utils/cleanText';

import {
  appendAiMessage,
  clearAiConversation,
  getAiUsage,
  setAiHandoff,
} from 'modules/ai/redux/aiActions';
import { AI_EVENTS } from 'modules/ai/constants/aiAnalyticsEvents';
import { AiEmbedContext } from 'modules/ai/constants/embedAiSuggestions';
import { useAiChatStream } from 'modules/ai/hooks/useAiChatStream';
import { useNavigateToAiAgent } from 'modules/ai/hooks/useNavigateToAiAgent';
import trackAiActivity from 'modules/ai/utils/trackAiActivity';
import AiQuotaExceededModal from 'modules/ai/modals/AiQuotaExceededModal';
import { AiQuotaExceededModalProvider } from 'modules/ai/modals/AiQuotaExceededModal/context/AiQuotaExceededModalContext';
import { createMessageId } from 'modules/ai/pages/SocietyAiAgent/utils/createMessageId';

type UseEmbedAiComposerParams = {
  societyId: string;
  embedContext?: AiEmbedContext;
};

export const useEmbedAiComposer = ({
  societyId,
  embedContext = 'society',
}: UseEmbedAiComposerParams) => {
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();
  const navigateToAiAgent = useNavigateToAiAgent();

  const [inputText, setInputText] = useState('');
  const [lastSuggestionKey, setLastSuggestionKey] = useState<string | null>(
    null
  );
  const conversationIdRef = useRef<string | null>(null);
  const handoffStartedAtRef = useRef<number | null>(null);
  const pendingAssistantMessageIdRef = useRef<string | null>(null);

  const isStreaming = useSelector((state: State) => state.ai?.isStreaming ?? false);
  const usage = useSelector((state: State) => state.ai?.usage);

  const { sendMessage, error } = useAiChatStream();

  useEffect(() => {
    if (error === 'AI_QUOTA_EXCEEDED' && usage) {
      dispatch(
        setModal(
          <AiQuotaExceededModalProvider
            used={usage.used || 0}
            limit={usage.limit || 0}
            tier={usage.tier || SocietyAiPlanTier.FREE}
          >
            <AiQuotaExceededModal />
          </AiQuotaExceededModalProvider>
        )
      );
    }
  }, [dispatch, error, usage]);

  const selectSuggestion = useCallback(
    (text: string, suggestionKey?: string) => {
      setInputText(cleanText(text));
      if (suggestionKey) {
        setLastSuggestionKey(suggestionKey);
        trackAiActivity({
          mixpanel,
          state: store.getState() as State,
          eventName: AI_EVENTS.SUGGESTION_CLICKED,
          extras: {
            surface: 'embed',
            embedContext,
            suggestionKey,
            suggestionText: text,
          },
        });
      }
    },
    [embedContext, mixpanel]
  );

  const handleSubmit = useCallback(async () => {
    const trimmed = inputText.trim();
    if (!trimmed || !societyId || isStreaming) return;

    const userMessageId = createMessageId('u');
    const assistantMessageId = createMessageId('a');
    pendingAssistantMessageIdRef.current = assistantMessageId;
    handoffStartedAtRef.current = performance.now();
    conversationIdRef.current = null;

    dispatch(clearAiConversation() as any);
    dispatch(
      appendAiMessage({
        id: userMessageId,
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
      })
    );
    dispatch(setAiHandoff(true));
    setInputText('');

    const isSuggestion = lastSuggestionKey != null;

    try {
      await sendMessage({
        societyId,
        conversationId: null,
        message: trimmed,
        userMessageId,
        assistantMessageId,
        conversationTurn: 1,
        isFirstMessage: true,
        analyticsContext: {
          entryPoint: 'embed',
          embedContext,
          isSuggestion,
          suggestionKey: lastSuggestionKey || undefined,
          entrySource: 'embed',
        },
        persistOnUnmount: true,
        onConversationId: (id) => {
          conversationIdRef.current = id;
        },
        onFirstChunk: () => {
          trackAiActivity({
            mixpanel,
            state: store.getState() as State,
            eventName: AI_EVENTS.HANDOFF_COMPLETED,
            extras: {
              surface: 'embed',
              embedContext,
              conversationId: conversationIdRef.current,
              messageId: pendingAssistantMessageIdRef.current,
              timeToFirstChunkMs:
                handoffStartedAtRef.current != null
                  ? Math.round(performance.now() - handoffStartedAtRef.current)
                  : null,
            },
          });
          navigateToAiAgent({ societyId, handoff: true });
        },
        onDone: () => {
          dispatch(getAiUsage(societyId) as any);
          setLastSuggestionKey(null);
        },
      });
    } catch {
      // quota exceeded handled via error effect above
    }
  }, [
    dispatch,
    embedContext,
    inputText,
    isStreaming,
    lastSuggestionKey,
    mixpanel,
    navigateToAiAgent,
    sendMessage,
    societyId,
  ]);

  return {
    inputText,
    isStreaming,
    selectSuggestion,
    handleSubmit,
    setInputText,
  };
};
